import { type ConfiguredQuickEntryItem, type QuickEntryItem } from "../types/models";
import { type ThunkAction } from "redux-thunk";
import { type RootState } from "~/app/store";
import { type AnyAction } from "~/app/store/types";
import { type LineItemBuilder } from "~/utils/cartUpdating/LineItemUpdate";
import { type LineItem, PackageEntrySource } from "~/features/cart/cart/models/CartData";
import { getNextUID } from "~/common/utils/uid";
import * as WeightUtils from "~/utils/WeightUtils";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { newChangeObject } from "~/utils/cartUpdating/common";
import { groupByFn, toMapFn } from "~/common/utils/reducers";
import { refreshDeliveriesForProduct } from "~/utils/cartUpdating/delivery/refreshEdd";
import {
  MSG_COULD_NOT_PRESELECT_PACKAGE,
  MSG_PACKAGE_CHANGED,
  refreshAvailabilityLoadNonContract,
} from "~/utils/cartUpdating/refreshPackage";
import { refreshPriceInfoData } from "~/utils/cartUpdating/refreshPriceInfoData";
import { refreshDeliveryDate } from "~/utils/cartUpdating/delivery/refreshDate";
import { validatePackage, validateQty } from "~/utils/cartUpdating/validations/validateGeneralLineItem";
import { buildCartMaterialNos } from "~/utils/cartUpdating/common";
import { validateEntireProductQty } from "~/utils/cartUpdating/validations/validateGeneralProduct";
import { sendToHybris } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import { INVALID_ENTRY_ID } from "../constants/constants";
import { hasProductConfigured } from "../utils/helpers";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import { getProductData, isKITProduct, type ProductData } from "~/domains/product/types/ProductData";
import { cartPageLoadThunk } from "~/features/cart/cart/redux/cartPageLoadThunk";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { fetchProductInfo } from "~/domains/product/api/fetchProductInfo";
import { loadCartPna, resolveCartDescriptor } from "~/features/cart/cart/pna/cartPna";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

const normalizePriceReference = (priceReference?: string) => (priceReference === "-" ? "" : priceReference ?? "");

export const addProductsToCartAction = (items: QuickEntryItem[]): ThunkResult<Promise<void>> => {
  return (dispatch, getState) => {
    if (userIsCustomer()) {
      return dispatch(addProductsToCartActionB2B(items));
    } else {
      return dispatch(addProductsToCartActionB2C(items));
    }
  };
};

export const addProductsToCartActionB2B = (items: QuickEntryItem[]): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    // Sanity check - items without products should not be passed here
    const configuredEntries = items.filter(hasProductConfigured);

    if (configuredEntries.length === 0) {
      return;
    }

    const state = getState();

    const getPriceRef = (item: ConfiguredQuickEntryItem) => normalizePriceReference(item.priceReference);

    // PnA through the canonical queries (A07): one load per distinct (code, priceReference) pair,
    // including KIT optional parts; descriptors are built from the returned pairs.
    const requests = configuredEntries.flatMap((item) => {
      const priceReference = getPriceRef(item);
      const currentBase = state.products.basic[item.product.code];
      return [
        { productCode: item.product.code, priceReference },
        ...(isKITProduct(currentBase)
          ? currentBase.optionalParts.map((subProduct) => ({
              productCode: subProduct.product.code,
              priceReference,
            }))
          : []),
      ];
    });

    // KIT sub-parts are never searched individually — stage their base products before the PnA
    // load so transports and descriptors carry a base (mirrors ProductService.ensureBaseProductData).
    await Promise.all(
      [...new Set(requests.map((request) => request.productCode))].map(async (productCode) => {
        if (getState().products.basic[productCode]) {
          return;
        }
        const product = await fetchProductInfo(productCode);
        dispatch(addBaseProductDataAction(product));
      }),
    );

    const pairs = await Promise.all(
      requests.map(async ({ productCode, priceReference }) => ({
        productCode,
        priceReference,
        pair: await loadCartPna({ productCode, priceReference }).catch(() => undefined),
      })),
    );

    const descriptorsByCodeByPriceRef: Dict<Dict<ProductDescriptor>> = {};
    pairs.forEach(({ productCode, priceReference, pair }) => {
      descriptorsByCodeByPriceRef[productCode] = descriptorsByCodeByPriceRef[productCode] ?? {};
      descriptorsByCodeByPriceRef[productCode][priceReference] = resolveCartDescriptor(
        getState(),
        productCode,
        priceReference,
        pair,
      );
    });

    const transform = (item: ConfiguredQuickEntryItem) =>
      transformItem(item, descriptorsByCodeByPriceRef[item.product?.code || ""][getPriceRef(item)]);

    const transformedEntries = configuredEntries.map(transform);
    let allNewLineItems = transformedEntries.map((_) => _.lineItem);
    const allNewOptionalParts: LineItemBuilder[] = transformedEntries.flatMap((_) => _.subLineItems);

    allNewLineItems = allNewLineItems.concat(allNewOptionalParts);

    const newLineItemsByCode = groupByFn(
      allNewLineItems,
      (item) => item.getProductCode(),
      (item) => item,
    );

    const existingLineItemsByCode = groupByFn(
      Object.values(state.cart.packageEntriesById).map(newChangeObject),
      (item) => item.getProductCode(),
      (item) => item,
    );

    // These need to go through a full refresh
    const merged = Object.keys(newLineItemsByCode).reduce(
      (map, productCode) => {
        map[productCode] = [...(existingLineItemsByCode[productCode] || []), ...newLineItemsByCode[productCode]];
        return map;
      },
      {} as Dict<LineItemBuilder[]>,
    );

    Object.keys(merged).forEach((productCode) => {
      orchestrateRefreshProductForATC(
        merged[productCode],
        descriptorsByCodeByPriceRef[productCode],
        buildCartMaterialNos(state),
      );
    });

    // We are expected to preselect the items, so no message
    allNewLineItems.forEach((item) => {
      item.removeMessage(MSG_COULD_NOT_PRESELECT_PACKAGE.key);
      item.removeMessage(MSG_PACKAGE_CHANGED.key);
    });

    // New rows have no server identity yet; publish them only through the authoritative reload.
    await Promise.all(
      Object.values(merged).map((lineItems) => {
        return dispatch(sendToHybris(lineItems.filter((item) => !allNewOptionalParts.includes(item))));
      }),
    );
    await dispatch(cartPageLoadThunk());
  };
};

export const addProductsToCartActionB2C = (items: QuickEntryItem[]): ThunkResult<Promise<void>> => {
  return async (dispatch, getState) => {
    // Sanity check - items without products should not be passed here
    const configuredEntries = items.filter(hasProductConfigured);

    if (configuredEntries.length === 0) {
      return;
    }

    const state = getState();

    const products = await Promise.all(configuredEntries.map((item) => fetchProductInfo(item.product.code)));
    products.forEach((product) => dispatch(addBaseProductDataAction(product)));
    const productByCode = toMapFn(products, (item) => item.code);

    const transform = (item: ConfiguredQuickEntryItem) => transformItem(item, productByCode[item.product.code]);

    const transformedEntries = configuredEntries.map(transform);
    let allNewLineItems = transformedEntries.map((_) => _.lineItem);
    const allNewOptionalParts: LineItemBuilder[] = transformedEntries.flatMap((_) => _.subLineItems);

    allNewLineItems = allNewLineItems.concat(allNewOptionalParts);

    const newLineItemsByCode = groupByFn(
      allNewLineItems,
      (item) => item.getProductCode(),
      (item) => item,
    );

    const existingLineItemsByCode = groupByFn(
      Object.values(state.cart.packageEntriesById).map(newChangeObject),
      (item) => item.getProductCode(),
      (item) => item,
    );

    // These need to go through a full refresh
    const merged = Object.keys(newLineItemsByCode).reduce(
      (map, productCode) => {
        map[productCode] = [...(existingLineItemsByCode[productCode] || []), ...newLineItemsByCode[productCode]];
        return map;
      },
      {} as Dict<LineItemBuilder[]>,
    );

    const allLineItems = Object.values(merged).flat();

    // We are expected to preselect the items, so no message
    allNewLineItems.forEach((item) => {
      item.removeMessage(MSG_COULD_NOT_PRESELECT_PACKAGE.key);
      item.removeMessage(MSG_PACKAGE_CHANGED.key);
    });

    // Force item.hasUpdate to be true
    allLineItems.forEach((item) => {
      item.set("productCode", item.getProductCode());
    });

    // New rows have no server identity yet; publish them only through the authoritative reload.
    await Promise.all(Object.values(merged).map((lineItems) => dispatch(sendToHybris(lineItems))));
    await dispatch(cartPageLoadThunk());
  };
};

function orchestrateRefreshProductForATC(
  allLineItems: LineItemBuilder[],
  descriptorsByPriceReference: Dict<ProductDescriptor>,
  cartMaterialNos?: Set<string>,
) {
  const anyDescriptor = Object.values(descriptorsByPriceReference)[0];
  const availability = anyDescriptor.availability;

  const lineItemsByType = groupByFn(
    allLineItems,
    (item) => item.get("source"),
    (item) => item,
  );
  const lineItemsWithUpdateablePrice = [
    ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
    ...(lineItemsByType[PackageEntrySource.BULK] || []),
  ];
  const lineItemsWithUpdateableEDD = [
    ...(lineItemsByType[PackageEntrySource.DEFAULT] || []),
    ...(lineItemsByType[PackageEntrySource.BULK] || []),
    ...(lineItemsByType[PackageEntrySource.QUOTE] || []),
  ];

  // 2.1. First availability => We may need to preselect packages.
  lineItemsWithUpdateablePrice.forEach((lineItem) =>
    refreshAvailabilityLoadNonContract(lineItem, availability!, anyDescriptor),
  );

  // 2.2. Then prices.
  refreshPriceInfoData(allLineItems, descriptorsByPriceReference);

  // 3. Refresh the Estimated Delivery Date.
  refreshDeliveriesForProduct(lineItemsWithUpdateableEDD, availability!);

  // 4. Refresh requested delivery date
  allLineItems.forEach(refreshDeliveryDate);

  // 5. Various qty and package validations
  allLineItems.forEach(validateQty);
  validateEntireProductQty(allLineItems, Object.values(descriptorsByPriceReference)[0], cartMaterialNos);
  allLineItems.forEach(validatePackage);
}

function transformItem(
  data: QuickEntryItem,
  productDescriptor: ProductDescriptor | ProductData,
): { lineItem: LineItemBuilder; subLineItems: LineItemBuilder[] } {
  const product = getProductData(productDescriptor);
  const item: LineItem = {
    ...makeEmptyLineItem(product.code),
    comment: data.comment,
    deliveryDate: data.requestedDeliveryDate,
    packCode: data.packCode ? data.packCode : INVALID_PACKAGE_CODE,
    quantity: data.quantity,
    weight: product.weight ?? WeightUtils.zero(),
    priceReference: normalizePriceReference(data.priceReference),

    restrictedPackage: isPackageRestrictionEnabled() ? !!data.packCode : false,
  };

  return {
    lineItem: newChangeObject(item),
    subLineItems: [],
  };
}

const makeEmptyLineItem = (productCode: string): LineItem => {
  return {
    pk: INVALID_ENTRY_ID,
    id: getNextUID(),
    cartEntryPk: INVALID_ENTRY_ID,
    productCode: productCode,
    groupNumbers: [],
    comment: "",
    deliveryDate: new Date(),
    manuallySetRDD: true,
    packSize: 1,
    packCode: INVALID_PACKAGE_CODE,
    quantity: 1,
    weight: WeightUtils.zero(),
    netPrice: undefined,
    valid: true,
    entryNumber: null,
    primaryWarehouseName: "",
    primaryWarehouseCode: "",
    messages: [],
    disableEdit: false,
    source: PackageEntrySource.DEFAULT,
    showComments: false,
    priceReference: "",

    edd: null,
    isInStock: null,

    // Salesman fields
    internalComment: "",
    priceOverriden: false,

    // quote fields
    quoteNumber: "",
    quoteLine: "",
    minimumQty: 1,
    contractNumber: "",
    contractLine: "",
    estimatedShippingDate: null,
    quoteExpiryDate: null,
    original: true,

    // UI Fields
    isPackagingSelectionOpen: false,

    restrictedPackage: false,

    customerOrderNumber: "",
    countryOfOrigin: "",

    tariffSurcharge: false,
  };
};
